import type { Task } from "@app/core";
import { useRef, useState, type MouseEvent } from "react";
import { useTaskDialogStore, useTasksStore } from "../../stores/index.js";
import { isCalendarBackgroundCreateSuppressed } from "./calendar-background-create-guard.js";
import { getDragTaskId, occurrenceIsoOf, rescheduleTo, splitVirtualId } from "./drag.js";
import { addDaysLocal, dayStart, spanDays, taskEndDay } from "./month-helpers.js";
import { resolveSource } from "./resolve.js";
import { useRecurringTaskActions } from "./useRecurringTaskActions.js";
import { useWindowMouseUp } from "./useWindowMouseUp.js";

export type DragState =
  | { kind: "idle" }
  | { kind: "create"; start: Date; end: Date }
  | { kind: "move"; task: Task; startDate: Date; end: Date }
  | { kind: "resize-left"; task: Task; taskEnd: Date; end: Date }
  | { kind: "resize-right"; task: Task; taskStart: Date; end: Date };

interface UseMonthDragResult {
  isDragHighlighted: (cellDate: Date) => boolean;
  handleDrop: (e: React.DragEvent, day: Date) => Promise<void>;
  startCreate: (e: MouseEvent, cellDate: Date) => void;
  onCellEnter: (cellDate: Date) => void;
  startMove: (e: MouseEvent, task: Task, grabDate: Date) => void;
  startResizeLeft: (e: MouseEvent, task: Task) => void;
  startResizeRight: (e: MouseEvent, task: Task) => void;
}

export function useMonthDrag(): UseMonthDragResult {
  const openEdit = useTaskDialogStore((s) => s.openEdit);
  const openCreate = useTaskDialogStore((s) => s.openCreate);
  const addTask = useTasksStore((s) => s.add);
  const editTask = useTasksStore((s) => s.edit);
  const actions = useRecurringTaskActions();
  const onMouseUpOnce = useWindowMouseUp();

  const [drag, setDrag] = useState<DragState>({ kind: "idle" });
  const dragRef = useRef<DragState>({ kind: "idle" });

  function setDragState(d: DragState) {
    dragRef.current = d;
    setDrag(d);
  }

  async function handleDrop(e: React.DragEvent, day: Date) {
    e.preventDefault();
    const draggedId = getDragTaskId(e);
    if (!draggedId) return;

    const { sourceId, occIso } = splitVirtualId(draggedId);
    // Read LIVE — memoized month cells keep stale handler closures by design.
    const source = useTasksStore.getState().tasks.find((t: Task) => t.id === sourceId);
    if (!source) return;

    // Time-blocking an undated one-off (inbox / nested subtask) onto month → an
    // all-day placement on that day.
    if (!source.recurrence_id && !source.due_at) {
      await editTask(source.id, {
        due_at: dayStart(day).toISOString(),
        all_day: true,
        duration_minutes: source.duration_minutes > 0 ? source.duration_minutes : 1440,
      });
      return;
    }

    const oldOcc = occIso ?? source.due_at;
    const newIso = rescheduleTo(oldOcc ?? null, day);
    // Dropped back on its own day: the reschedule maps to the exact same instant,
    // so there's nothing to change — skip the edit AND the recurring this/
    // following/all confirm (mirrors the timed-grid no-op guard).
    if (oldOcc && newIso === oldOcc) return;
    await actions.rescheduleTask({ source, oldOcc, newIso, isVirtual: occIso !== null });
  }

  function startCreate(e: MouseEvent, cellDate: Date) {
    e.stopPropagation();
    if (e.button !== 0) return;
    if (isCalendarBackgroundCreateSuppressed() || useTaskDialogStore.getState().open) return;
    const startD = dayStart(cellDate);
    setDragState({ kind: "create", start: startD, end: startD });

    onMouseUpOnce(() => {
      const d = dragRef.current;
      setDragState({ kind: "idle" });
      if (d.kind !== "create") return;
      const lo = d.start <= d.end ? d.start : d.end;
      const hi = d.start <= d.end ? d.end : d.start;
      const spanCount = Math.round((hi.getTime() - lo.getTime()) / 86400000) + 1;
      if (spanCount === 1) openCreate(lo);
      else void addTask({ title: "", due_at: lo.toISOString(), all_day: true, duration_minutes: spanCount * 1440 }).then((t) => openEdit(t));
    });
  }

  function onCellEnter(cellDate: Date) {
    const d = dragRef.current;
    if (d.kind === "idle") return;
    setDragState({ ...d, end: dayStart(cellDate) } as DragState);
  }

  function startMove(e: MouseEvent, task: Task, grabDate: Date) {
    e.stopPropagation();
    if (e.button !== 0 || !task.due_at) return;
    const startDate = dayStart(grabDate);
    setDragState({ kind: "move", task, startDate, end: startDate });

    onMouseUpOnce(() => {
      const d = dragRef.current;
      setDragState({ kind: "idle" });
      if (d.kind !== "move" || !d.task.due_at) return;
      const delta = Math.round((d.end.getTime() - d.startDate.getTime()) / 86400000);
      if (delta === 0) return;
      // Move the dragged occurrence (not the series base), and route recurring
      // tasks through the same confirm + series ops as the drop/edit flows.
      const draggedOcc = occurrenceIsoOf(d.task);
      if (!draggedOcc) return;
      const newIso = addDaysLocal(new Date(draggedOcc), delta).toISOString();
      const source = resolveSource(d.task, useTasksStore.getState().tasks);
      void actions.rescheduleTask({ source, oldOcc: draggedOcc, newIso, isVirtual: !!d.task.virtual });
    });
  }

  function startResizeLeft(e: MouseEvent, task: Task) {
    e.stopPropagation();
    if (e.button !== 0 || !task.due_at) return;
    setDragState({ kind: "resize-left", task, taskEnd: taskEndDay(task), end: dayStart(new Date(task.due_at)) });

    onMouseUpOnce(() => {
      const d = dragRef.current;
      setDragState({ kind: "idle" });
      if (d.kind !== "resize-left" || !d.task.due_at) return;
      const newStart = d.end <= d.taskEnd ? d.end : d.taskEnd;
      if (newStart.getTime() === dayStart(new Date(d.task.due_at)).getTime()) return; // unchanged
      const nDays = Math.round((d.taskEnd.getTime() - newStart.getTime()) / 86400000) + 1;
      // Route through the shared recurring dispatch (like startMove): editing a
      // virtual occurrence's id directly would find nothing in the store and
      // silently no-op. Non-recurring falls through to the same plain edit.
      const source = resolveSource(d.task, useTasksStore.getState().tasks);
      const occKey = occurrenceIsoOf(d.task);
      if (!occKey) return;
      void actions.resizeTaskFromTop({ source, occKey, newStartIso: newStart.toISOString(), newMinutes: nDays * 1440, isVirtual: !!d.task.virtual });
    });
  }

  function startResizeRight(e: MouseEvent, task: Task) {
    e.stopPropagation();
    if (e.button !== 0 || !task.due_at) return;
    setDragState({ kind: "resize-right", task, taskStart: dayStart(new Date(task.due_at)), end: taskEndDay(task) });

    onMouseUpOnce(() => {
      const d = dragRef.current;
      setDragState({ kind: "idle" });
      if (d.kind !== "resize-right" || !d.task.due_at) return;
      const newEnd = d.end >= d.taskStart ? d.end : d.taskStart;
      const nDays = Math.round((newEnd.getTime() - d.taskStart.getTime()) / 86400000) + 1;
      if (nDays === spanDays(d.task)) return; // unchanged
      const source = resolveSource(d.task, useTasksStore.getState().tasks);
      void actions.resizeTask({ source, occKey: d.task.due_at, newMinutes: nDays * 1440, isVirtual: !!d.task.virtual });
    });
  }

  function isDragHighlighted(cellDate: Date): boolean {
    if (drag.kind === "idle") return false;
    const cell = dayStart(cellDate);
    if (drag.kind === "create") {
      const lo = drag.start <= drag.end ? drag.start : drag.end;
      const hi = drag.start <= drag.end ? drag.end : drag.start;
      return cell >= lo && cell <= hi;
    }
    if (drag.kind === "move") {
      if (!drag.task.due_at) return false;
      const delta = Math.round((drag.end.getTime() - drag.startDate.getTime()) / 86400000);
      const newStart = addDaysLocal(dayStart(new Date(drag.task.due_at)), delta);
      return cell >= newStart && cell <= addDaysLocal(newStart, spanDays(drag.task) - 1);
    }
    if (drag.kind === "resize-left") {
      const newStart = drag.end <= drag.taskEnd ? drag.end : drag.taskEnd;
      return cell >= newStart && cell <= drag.taskEnd;
    }
    if (drag.kind === "resize-right") {
      const newEnd = drag.end >= drag.taskStart ? drag.end : drag.taskStart;
      return cell >= drag.taskStart && cell <= newEnd;
    }
    return false;
  }

  return { isDragHighlighted, handleDrop, startCreate, onCellEnter, startMove, startResizeLeft, startResizeRight };
}
